import { Medidor } from '../../domain/entities/Medidor';
import { crearError, registrarError, traducirErrorSupabase } from '../../domain/errores';
import { aBooleano, aFecha, aNumero, aObjeto, aTexto } from '../mappers/conversiones';
import { supabase } from '../supabase/cliente';
import { ambitoSesion } from './AmbitoSesion';
import { filtroDemo } from './FiltroDemo';
import { leerNombres } from './consultas';

export type NumeroRele = 1 | 2;

/** Configuración con la que se da de alta o se edita un medidor. */
export interface DatosMedidor {
  cliente_id: number;
  meter_serial: string;
  meter_name: string;
  source: string;
  protocol: string;
  gateway_id: string | null;
  /** Nombre del secreto con la credencial; el valor vive en el servidor. */
  api_key_ref: string;
  nodo_id: number | null;
  poll_enabled: boolean;
  poll_interval_s: number;
}

/** Orígenes y protocolos que admite el backend. */
export const ORIGENES_MEDIDOR = [{ valor: 'ekm_push3', etiqueta: 'EKM Push3' }] as const;

export const PROTOCOLOS_MEDIDOR = [
  { valor: 'v3', etiqueta: 'v3 — sólo lectura' },
  { valor: 'v4', etiqueta: 'v4 — lectura y corte de energía' },
] as const;

/** Cada cuánto se lee el medidor, en segundos. */
export const INTERVALO_SONDEO_MINIMO_S = 30;
export const INTERVALO_SONDEO_MAXIMO_S = 3600;
export const INTERVALO_SONDEO_POR_DEFECTO_S = 60;

/**
 * Acceso a los medidores de energía y a sus lecturas.
 *
 * La configuración de cada medidor y su última lectura viven en tablas
 * separadas; el repositorio las une para entregar medidores completos.
 */
export class RepositorioMedidores {
  async listar(clienteId?: number | null): Promise<Medidor[]> {
    let consulta = filtroDemo.aplicar(
      'smart_meter_config',
      supabase
        .from('smart_meter_config')
        .select(
          'id, cliente_id, meter_serial, meter_name, source, protocol, gateway_id, api_key_ref, nodo_id, poll_enabled, poll_interval_s',
        )
        .order('meter_name', { ascending: true }),
    );

    consulta = ambitoSesion.aplicar(consulta);
    if (clienteId) consulta = consulta.eq('cliente_id', clienteId);

    const { data, error } = await consulta;
    if (error) throw traducirErrorSupabase(error);

    const configuraciones = (data ?? []) as Record<string, unknown>[];
    if (configuraciones.length === 0) return [];

    const lecturas = await this.leerUltimasLecturas();
    const nombresCliente = await leerNombres(
      'clientes',
      configuraciones.map((c) => aNumero(c.cliente_id)).filter((id): id is number => id !== null),
    );

    return configuraciones.map((fila) => {
      // El identificador del medidor es un UUID; convertirlo a número lo
      // anularía y las lecturas nunca encontrarían a su medidor.
      const id = aTexto(fila.id) ?? '';
      const clienteId = aNumero(fila.cliente_id);
      const lectura = lecturas.get(id);

      return new Medidor({
        id,
        serial: aTexto(fila.meter_serial),
        nombre: aTexto(fila.meter_name),
        clienteId,
        clienteNombre: clienteId !== null ? (nombresCliente.get(clienteId) ?? null) : null,
        origen: aTexto(fila.source),
        protocolo: aTexto(fila.protocol),
        gateway: aTexto(fila.gateway_id),
        claveMedicion: aTexto(fila.api_key_ref),
        nodoId: aNumero(fila.nodo_id),
        sondeoActivo: aBooleano(fila.poll_enabled) ?? false,
        intervaloSondeoSegundos: aNumero(fila.poll_interval_s),
        lecturas: lectura?.datos ?? {},
        ultimaLectura: lectura?.fecha ?? null,
        lecturaCorrecta: lectura?.correcta ?? null,
        mensajeError: lectura?.error ?? null,
      });
    });
  }

  /**
   * Últimas lecturas de todos los medidores visibles.
   * Las políticas de la base ya limitan lo que cada usuario puede ver, así que
   * no hace falta filtrar aquí.
   */
  private async leerUltimasLecturas(): Promise<
    Map<
      string,
      {
        datos: Record<string, unknown>;
        fecha: Date | null;
        correcta: boolean | null;
        error: string | null;
      }
    >
  > {
    const mapa = new Map<
      string,
      {
        datos: Record<string, unknown>;
        fecha: Date | null;
        correcta: boolean | null;
        error: string | null;
      }
    >();

    const { data, error } = await supabase
      .from('last_meter_values')
      .select('meter_id, data, last_read_at, read_ok, error_msg');

    if (error) {
      registrarError('medidores.ultimasLecturas', error);
      return mapa;
    }

    for (const fila of (data ?? []) as Record<string, unknown>[]) {
      const meterId = aTexto(fila.meter_id);
      if (meterId === null) continue;

      mapa.set(meterId, {
        datos: aObjeto(fila.data),
        fecha: aFecha(fila.last_read_at),
        correcta: aBooleano(fila.read_ok),
        error: aTexto(fila.error_msg),
      });
    }

    return mapa;
  }

  /**
   * Abre o cierra un relé del medidor.
   *
   * Va por una función del servidor porque hablar con el medidor exige una
   * clave que no puede estar en el navegador. Esa función además comprueba
   * permisos y deja registro de quién accionó qué.
   */
  async accionarRele(medidorId: string, rele: NumeroRele, encender: boolean): Promise<void> {
    const { data, error } = await supabase.functions.invoke('smart-meter-command', {
      body: {
        meter_id: medidorId,
        command: 'SetRelay',
        params: { relay: rele, status: encender ? 'ON' : 'OFF', duration: 0 },
      },
    });

    if (error) {
      registrarError('medidores.accionarRele', error);
      throw crearError('DESCONOCIDO', error, 'No se pudo enviar la orden al medidor.');
    }

    // La función responde 200 aunque el medidor rechace la orden; hay que
    // mirar el cuerpo para saber si de verdad se aplicó.
    const respuesta = aObjeto(data);
    if (respuesta.ok === false || respuesta.success === false) {
      const detalle = aTexto(respuesta.error) ?? aTexto(respuesta.message);
      throw crearError('DESCONOCIDO', data, detalle ?? 'El medidor rechazó la orden.');
    }
  }
  /** Da de alta un medidor nuevo. */
  async crear(datos: DatosMedidor): Promise<void> {
    const { error } = await supabase.from('smart_meter_config').insert(datos);
    if (error) throw traducirErrorSupabase(error);
  }

  /**
   * Cambia la configuración de un medidor.
   *
   * El serial y el cliente no se tocan: identifican al aparato. Cambiarlos
   * sería dar de alta otro medidor, no editar este.
   */
  async actualizar(id: string, datos: Partial<DatosMedidor>): Promise<void> {
    const { cliente_id: _cliente, meter_serial: _serial, ...editable } = datos;

    const { error } = await supabase.from('smart_meter_config').update(editable).eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  async eliminar(id: string): Promise<void> {
    const { error } = await supabase.from('smart_meter_config').delete().eq('id', id);
    if (error) throw traducirErrorSupabase(error);
  }

  /** Enciende o pausa la lectura automática del medidor. */
  async cambiarSondeo(id: string, activo: boolean): Promise<void> {
    await this.actualizar(id, { poll_enabled: activo });
  }
}

export const repositorioMedidores = new RepositorioMedidores();
